import type { KeyboardEvent } from 'react';
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react';

import { CapacitySlots } from '@/features/concurrency';
import { formatMoney, THRESHOLD_FILL } from '@/features/workspace-usage';
import { PhoneCard, PhoneCardList } from '@/components/ui/phone/PhoneCardList';
import { DeletedLabel } from '@/shared/components/DeletedLabel';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';

import { formatMemory } from '../memoryLimit';
import {
  REASON_WORDS,
  limitSpendOf,
  type LimitsSort,
  type LimitsSortKey,
  type WorkspaceLimitsRow,
} from '../workspaceLimits';

export interface WorkspaceLimitsTableProps {
  rows: readonly WorkspaceLimitsRow[];
  hasRuns: boolean;
  /** Whether the service reports memory; without it the column is absent. */
  hasMemory?: boolean;
  /** The workspace whose sheet is open. */
  selectedId?: string;
  onSelect: (workspaceId: string) => void;
  sort?: LimitsSort;
  onSortChange?: (sort: LimitsSort | undefined) => void;
}

function spendText(row: WorkspaceLimitsRow): string {
  const spent = formatMoney(row.spend, row.currency);
  // A deleted workspace has no limit to be measured against (spec 146).
  if (row.deleted) return spent;
  const limit =
    row.spendLimit === null ? null : formatMoney(row.spendLimit, row.currency);
  // A Daily limit read in October is judged by today, and says so (spec 237).
  const text =
    limit === null
      ? `${spent} · no limit`
      : row.limitOwn
        ? `${formatMoney(row.limitOwn.spend, row.currency)} ${row.limitOwn.phrase} of ${limit}`
        : `${spent} of ${limit}`;
  return row.spendCustom ? `${text} · custom` : text;
}

function spendShare(row: WorkspaceLimitsRow): number {
  if (row.spendLimit === null || Number(row.spendLimit) <= 0) return 0;
  return Math.min(1, Number(limitSpendOf(row)) / Number(row.spendLimit));
}

function runsText(row: WorkspaceLimitsRow): string {
  const runs = row.runs;
  if (!runs) return '';
  let text = `${String(runs.running)} of ${String(runs.limit)}`;
  if (runs.queued > 0) text += ` · ${String(runs.queued)} waiting`;
  if (runs.source === 'workspace') text += ' · custom';
  return text;
}

/** Empty when the read says nothing about this workspace: an empty cell asserts nothing. */
function memoryText(row: WorkspaceLimitsRow): string {
  const memory = row.memory;
  if (!memory) return '';
  let text = formatMemory(memory.amountMib, memory.unit);
  if (memory.source === 'workspace') text += ' · custom';
  return text;
}

function capsText(count: number): string {
  if (count === 0) return '—';
  return count === 1 ? '1 cap' : `${String(count)} caps`;
}

/**
 * One row per workspace, everything that holds it back side by side (spec 117 US1).
 *
 * A row opens that workspace's sheet — by pointer, and by Enter or Space for a keyboard
 * (FR-019). The rows are the page's only interactive list, so they carry the focus ring
 * and `aria-selected` rather than a button in a cell that would say the same thing.
 */
export function WorkspaceLimitsTable({
  rows,
  hasRuns,
  hasMemory = false,
  selectedId,
  onSelect,
  sort,
  onSortChange,
}: WorkspaceLimitsTableProps) {
  const isPhone = usePhoneWidth();
  // FR-026 of spec 073, kept: two workspaces with one name say which is which.
  const seen = new Map<string, number>();
  for (const row of rows) seen.set(row.name, (seen.get(row.name) ?? 0) + 1);

  if (isPhone) {
    return (
      <WorkspaceLimitsCards
        rows={rows}
        seen={seen}
        selectedId={selectedId}
        onSelect={onSelect}
      />
    );
  }

  return (
    <div className="border-border bg-card shrink-0 overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[46rem] text-sm">
        <caption className="sr-only">
          Every workspace and the limits that hold it back
        </caption>
        <thead>
          <tr className="text-muted-foreground border-border border-b text-xs">
            <SortHeader
              label="Workspace"
              column="name"
              sort={sort}
              onSortChange={onSortChange}
            />
            <SortHeader
              label="Spend this period"
              column="spend"
              sort={sort}
              onSortChange={onSortChange}
            />
            {hasRuns ? (
              <SortHeader
                label="Runs now"
                column="runs"
                sort={sort}
                onSortChange={onSortChange}
              />
            ) : null}
            {hasMemory ? (
              <th scope="col" className="px-4 py-2 text-left font-medium">
                Memory per run
              </th>
            ) : null}
            <th scope="col" className="px-4 py-2 text-left font-medium">
              Workflow caps
            </th>
            <th scope="col" className="px-4 py-2 text-left font-medium">
              Needs a look
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const selected = row.workspaceId === selectedId;
            const warn =
              row.spendThreshold === 'approaching' ||
              row.spendThreshold === 'exhausted';
            return (
              <tr
                key={row.workspaceId}
                data-workspace-id={row.workspaceId}
                data-workspace-name={row.name}
                // A deleted workspace opens no sheet: nothing on it can be changed
                // (spec 146). Its spend stays, so its row does too.
                tabIndex={row.deleted ? undefined : 0}
                aria-selected={row.deleted ? undefined : selected}
                onClick={() => {
                  if (!row.deleted) onSelect(row.workspaceId);
                }}
                onKeyDown={(event: KeyboardEvent<HTMLTableRowElement>) => {
                  if (row.deleted) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(row.workspaceId);
                  }
                }}
                className={`border-border focus-visible:ring-ring border-t first:border-t-0 focus-visible:ring-3 focus-visible:outline-none ${
                  row.deleted
                    ? ''
                    : selected
                      ? 'cursor-pointer bg-selected'
                      : 'cursor-pointer hover:bg-accent/50'
                }`}
              >
                <th scope="row" className="px-4 py-2.5 text-left font-medium">
                  <span className="flex min-w-0 flex-col">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="truncate">{row.name}</span>
                      {row.deleted ? <DeletedLabel /> : null}
                    </span>
                    {(seen.get(row.name) ?? 0) > 1 ? (
                      <span className="text-muted-foreground font-mono text-xs font-normal">
                        <span className="sr-only">Identifier </span>
                        {row.workspaceId.slice(0, 8)}
                      </span>
                    ) : null}
                  </span>
                </th>
                <td className="px-4 py-2.5">
                  <span className="flex min-w-40 flex-col gap-1">
                    <span
                      aria-hidden="true"
                      className="bg-border h-1.5 overflow-hidden rounded-full"
                    >
                      <span
                        className={`block h-full rounded-full ${warn ? THRESHOLD_FILL.approaching : 'bg-primary'}`}
                        style={{ width: `${String(spendShare(row) * 100)}%` }}
                      />
                    </span>
                    <span className="text-muted-foreground text-xs tabular-nums">
                      {spendText(row)}
                    </span>
                  </span>
                </td>
                {hasRuns ? (
                  <td className="px-4 py-2.5">
                    {row.runs ? (
                      <span className="flex flex-col gap-1">
                        <CapacitySlots
                          running={row.runs.running}
                          limit={row.runs.limit}
                          queued={row.runs.queued}
                        />
                        <span className="text-muted-foreground text-xs tabular-nums">
                          {runsText(row)}
                          {row.runs.clamped ? ' · clamped' : ''}
                        </span>
                      </span>
                    ) : null}
                  </td>
                ) : null}
                {hasMemory ? (
                  <td className="text-muted-foreground px-4 py-2.5 text-xs tabular-nums">
                    {memoryText(row)}
                  </td>
                ) : null}
                <td className="text-muted-foreground px-4 py-2.5 text-xs">
                  {capsText(row.caps.length)}
                </td>
                <td className="px-4 py-2.5">
                  {row.reasons[0] ? (
                    <span className="bg-muted text-foreground rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap">
                      {REASON_WORDS[row.reasons[0]]}
                    </span>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The same rows as cards on a phone (spec 263): the spend bar at full width,
 * runs, memory and caps as one line, and what needs a look as a chip beside
 * the name. A deleted workspace's card opens nothing, as its row does.
 */
function WorkspaceLimitsCards({
  rows,
  seen,
  selectedId,
  onSelect,
}: {
  rows: readonly WorkspaceLimitsRow[];
  seen: ReadonlyMap<string, number>;
  selectedId?: string;
  onSelect: (workspaceId: string) => void;
}) {
  return (
    <PhoneCardList label="Every workspace and the limits that hold it back">
      {rows.map((row) => {
        const warn =
          row.spendThreshold === 'approaching' ||
          row.spendThreshold === 'exhausted';
        const limits = [
          row.runs
            ? `Runs ${runsText(row)}${row.runs.clamped ? ' · clamped' : ''}`
            : '',
          memoryText(row) ? `Memory ${memoryText(row)}` : '',
          row.caps.length > 0 ? capsText(row.caps.length) : '',
        ].filter(Boolean);
        return (
          <PhoneCard
            key={row.workspaceId}
            data-workspace-id={row.workspaceId}
            data-workspace-name={row.name}
            selected={!row.deleted && row.workspaceId === selectedId}
            onOpen={
              row.deleted
                ? undefined
                : () => {
                    onSelect(row.workspaceId);
                  }
            }
            title={row.name}
            badges={
              <>
                {row.deleted ? <DeletedLabel /> : null}
                {row.reasons[0] ? (
                  <span className="bg-muted text-foreground rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap">
                    {REASON_WORDS[row.reasons[0]]}
                  </span>
                ) : null}
              </>
            }
            lines={[
              (seen.get(row.name) ?? 0) > 1 ? (
                <span className="font-mono text-xs">
                  <span className="sr-only">Identifier </span>
                  {row.workspaceId.slice(0, 8)}
                </span>
              ) : null,
              <span className="flex flex-col gap-1">
                <span
                  aria-hidden="true"
                  className="bg-border h-1.5 overflow-hidden rounded-full"
                >
                  <span
                    className={`block h-full rounded-full ${warn ? THRESHOLD_FILL.approaching : 'bg-primary'}`}
                    style={{ width: `${String(spendShare(row) * 100)}%` }}
                  />
                </span>
                <span className="tabular-nums">{spendText(row)}</span>
              </span>,
              limits.length > 0 ? (
                <span className="tabular-nums">{limits.join(' · ')}</span>
              ) : null,
            ]}
          />
        );
      })}
    </PhoneCardList>
  );
}

/** A column header that orders the table: first press descending, then ascending, then off. */
function SortHeader({
  label,
  column,
  sort,
  onSortChange,
}: {
  label: string;
  column: LimitsSortKey;
  sort?: LimitsSort;
  onSortChange?: (sort: LimitsSort | undefined) => void;
}) {
  const active = sort?.key === column;
  const ariaSort = active
    ? sort.dir === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';
  if (!onSortChange) {
    return (
      <th scope="col" className="px-4 py-2 text-left font-medium">
        {label}
      </th>
    );
  }
  const next: LimitsSort | undefined = !active
    ? { key: column, dir: column === 'name' ? 'asc' : 'desc' }
    : sort.dir === (column === 'name' ? 'asc' : 'desc')
      ? { key: column, dir: column === 'name' ? 'desc' : 'asc' }
      : undefined;
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className="px-4 py-2 text-left font-medium"
    >
      <button
        type="button"
        onClick={() => {
          onSortChange(next);
        }}
        className="hover:text-foreground focus-visible:ring-ring touch-target inline-flex items-center gap-1 rounded-sm focus-visible:ring-3 focus-visible:outline-none"
      >
        {label}
        {active ? (
          sort.dir === 'asc' ? (
            <ArrowUpIcon aria-hidden="true" className="size-3" />
          ) : (
            <ArrowDownIcon aria-hidden="true" className="size-3" />
          )
        ) : null}
      </button>
    </th>
  );
}
